import { useLayoutEffect, useRef, useState } from "react";

import {
  TransformComponent,
  TransformWrapper,
  type ReactZoomPanPinchRef,
  type ReactZoomPanPinchState,
} from "react-zoom-pan-pinch";

import { Circle, cn, Loader } from "@eisox/design-system";
import { ChevronLeftIcon, ChevronRightIcon } from "@eisox/icons";

interface SchemeWrapperProps {
  className?: string;
  children: React.ReactNode;
  /** Blurs the diagram behind a centered loader. */
  loading?: boolean;
  /** Overlay rendered inside the zoom context, so it can use the react-zoom-pan-pinch hooks. */
  controls?: React.ReactNode;
}

const SchemeWrapper: React.FC<SchemeWrapperProps> = ({ className, children, loading = false, controls }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const schemeRef = useRef<HTMLDivElement>(null);

  const [{ scale, positionX, positionY }, setState] = useState<Omit<ReactZoomPanPinchState, "previousScale">>({
    scale: 1,
    positionX: 0,
    positionY: 0,
  });
  const [displayTranslateLeftButton, setDisplayTranslateLeftButton] = useState(false);
  const [displayTranslateRightButton, setDisplayTranslateRightButton] = useState(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const boilerroom = schemeRef.current;
    if (container === null || boilerroom === null) return;
    const containerHeight = container.getBoundingClientRect().height;
    const containerWidth = container.getBoundingClientRect().width;
    const boilerroomHeight = boilerroom.getBoundingClientRect().height;
    const boilerroomWidth = boilerroom.getBoundingClientRect().width;
    const scale = containerHeight / boilerroomHeight > 1 ? 1 : containerHeight / boilerroomHeight;

    const calculatedPositionX = containerWidth > boilerroomWidth ? (containerWidth - boilerroomWidth) / 2 : 0;
    const calculatedPositionY = (containerHeight - boilerroomHeight * scale) / 2;

    setState({
      scale,
      positionX: calculatedPositionX,
      positionY: calculatedPositionY,
    });
    setDisplayTranslateRightButton(containerWidth < boilerroomWidth * scale);
  }, [containerRef, schemeRef]);

  const handleTransform = (
    _: ReactZoomPanPinchRef,
    state: {
      scale: number;
      positionX: number;
      positionY: number;
    },
  ) => {
    const container = containerRef.current;
    const boilerroom = schemeRef.current;
    if (container !== null && boilerroom !== null) {
      const containerWidth = container.getBoundingClientRect().width;
      const boilerroomWidth = boilerroom.getBoundingClientRect().width;
      const conditionLeft = state.positionX < 0;
      const conditionRight = Math.round(boilerroomWidth - containerWidth + state.positionX) > 0;
      setDisplayTranslateLeftButton(containerWidth < boilerroomWidth && conditionLeft);
      setDisplayTranslateRightButton(containerWidth < boilerroomWidth && conditionRight);
    }
  };

  const handleMouseClick = (
    side: "right" | "left",
    setTransform: (newPositionX: number, newPositionY: number, newScale: number) => void,
    context: ReactZoomPanPinchRef,
  ) => {
    const container = containerRef.current;
    const boilerroom = schemeRef.current;
    if (container === null || boilerroom === null) return;
    const containerWidth = container.getBoundingClientRect().width;
    const boilerroomWidth = boilerroom.getBoundingClientRect().width;
    const translation = containerWidth - boilerroomWidth;
    setTransform(side === "right" ? translation : 0, context.state.positionY, context.state.scale);
  };

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden">
      <TransformWrapper
        initialScale={scale}
        initialPositionX={positionX}
        initialPositionY={positionY}
        minScale={scale}
        maxScale={1}
        onTransformed={handleTransform}
      >
        {({ setTransform, instance: { getContext } }) => (
          <>
            <TransformComponent
              wrapperStyle={{
                width: "100%",
                height: "100%",
                cursor: "grab",
                willChange: "transform",
              }}
            >
              <div ref={schemeRef} className={className} style={{ padding: 20 }}>
                {children}
              </div>
            </TransformComponent>
            {displayTranslateLeftButton && (
              <Circle.Root
                className="absolute top-1/2 left-[10px] -translate-y-1/2 cursor-pointer bg-primary-dark-blue text-primary-white"
                onClick={() => handleMouseClick("left", setTransform, getContext())}
              >
                <ChevronLeftIcon />
              </Circle.Root>
            )}
            {displayTranslateRightButton && (
              <Circle.Root
                className="absolute top-1/2 right-[10px] -translate-y-1/2 cursor-pointer bg-primary-dark-blue text-primary-white"
                onClick={() => handleMouseClick("right", setTransform, getContext())}
              >
                <ChevronRightIcon />
              </Circle.Root>
            )}
            {controls}
          </>
        )}
      </TransformWrapper>
      <div
        aria-hidden={!loading}
        className={cn(
          "inset-0 absolute z-10 flex items-center justify-center",
          "bg-primary-white/30 backdrop-blur-[3px] transition-opacity duration-150",
          loading ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      >
        {loading && <Loader />}
      </div>
    </div>
  );
};

SchemeWrapper.displayName = "SchemeWrapper";

export { SchemeWrapper };
export type { SchemeWrapperProps };
